import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Banknote,
  Boxes,
  Clock,
  Layers,
  MapPin,
  Package,
  Wrench,
} from "lucide-react";
import {
  fetchAdminInventory,
  fetchAdminInventorySummary,
  fetchAdminUnits,
  normalizeUnit,
  createInventoryItem,
  postInventoryImportPreview,
  fetchUnitInventoryAssignments,
  fetchAdminRooms,
} from "../../api/adminData";
import InventoryList from "./inventory/InventoryList";
import { getUnitAddressSubtitle, getUnitHeadingLabel } from "../../utils/unitDisplayId";
import { downloadInventoryCsv, inventoryStatusLabelDe } from "../../utils/inventoryExport";
import {
  ROOM_FILTER_ALL,
  ROOM_FILTER_SHARED,
  applyInventoryRoomFilter,
  buildRoomOptions,
} from "../../utils/inventoryRoomFilter";

function formatChf(value) {
  if (value == null || value === "") return "—";
  return `CHF ${Number(value).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Days (from API) → short label for KPI cards. */
function formatDaysToLabel(days) {
  if (days == null || days === undefined || Number.isNaN(Number(days))) return "—";
  const d = Math.round(Number(days));
  if (d < 0) return "—";
  if (d < 30) {
    if (d === 0) return "0 Tage";
    if (d === 1) return "1 Tag";
    return `${d} Tage`;
  }
  if (d <= 365) {
    const m = Math.max(1, Math.round(d / 30.437));
    return m === 1 ? "1 Monat" : `${m} Monate`;
  }
  const years = Math.floor(d / 365);
  const rem = d - years * 365;
  const months = Math.min(11, Math.max(0, Math.round(rem / 30.437)));
  if (months <= 0) {
    return years === 1 ? "1 J." : `${years} J.`;
  }
  return `${years} J., ${months} M.`;
}

/** Optional http(s) URL; empty → null. Returns { value } or { error }. */
function parseOptionalProductUrl(raw) {
  if (raw == null || String(raw).trim() === "") return { value: null };
  const s = String(raw).trim();
  try {
    const u = new URL(s);
    if (u.protocol !== "http:" && u.protocol !== "https:") {
      return { error: "Produkt-URL muss mit http:// oder https:// beginnen." };
    }
    return { value: s };
  } catch {
    return { error: "Ungültige Produkt-URL." };
  }
}

/**
 * One overview metric. Label, value, one short factual hint, one icon.
 *
 * No sparkline, no trend arrow, no percentage: the summary endpoint returns a
 * single snapshot with no time series behind it, so any of those would be
 * decoration standing in for data we do not have.
 *
 * `tone="warning"` tints only the icon, never the value. A number that turns
 * red reads as an alarm; these two are standing facts about the portfolio, and
 * the colour is a hint, not the information — the label carries that (SC 1.4.1).
 */
/** Shared look for every control in the filter bar, so they line up exactly. */
const FILTER_CONTROL_CLASS =
  "h-9 w-full rounded-[8px] border border-border bg-background px-2.5 text-[12.5px] " +
  "text-foreground placeholder:text-muted-foreground focus-visible:outline-none " +
  "focus-visible:ring-2 focus-visible:ring-ring";

/** Visible label above each control -- not a placeholder, so it survives typing. */
const FILTER_LABEL_CLASS =
  "text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground";


/**
 * One stock figure.
 *
 * S7c: a tinted icon tile leads, the label sits above a large number, and the
 * explanation closes the card — the reference's KPI composition. The contract
 * the characterization suite reads is untouched: still role="group" with the
 * label as its accessible name (S2, S3), and the value still inside the group's
 * textContent.
 *
 * `tone` only tints the tile. It never becomes the sole carrier of meaning —
 * the label says what the number is.
 */
const KPI_TILE = {
  default: "bg-muted text-muted-foreground",
  primary: "bg-primary/10 text-primary",
  success: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  warning: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
};

function KpiCard({ label, value, hint, icon: Icon, tone = "default" }) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex items-center gap-3 rounded-[12px] border border-border bg-card px-3.5 py-3"
    >
      {Icon ? (
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] ${
            KPI_TILE[tone] || KPI_TILE.default
          }`}
          aria-hidden
        >
          <Icon className="h-5 w-5" />
        </span>
      ) : null}
      <div className="min-w-0">
        <p className="m-0 truncate text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
          {label}
        </p>
        <p className="m-0 font-mono text-[22px] font-semibold leading-tight tabular-nums text-foreground">
          {value}
        </p>
        {hint ? (
          <p className="m-0 truncate text-[10.5px] leading-[1.4] text-muted-foreground">{hint}</p>
        ) : null}
      </div>
    </div>
  );
}


const INVENTORY_CATEGORIES = [
  "Wohnzimmer",
  "Schlafzimmer",
  "Küche",
  "Esszimmer",
  "Badezimmer",
  "Balkon / Terrasse",
  "Büro",
  "Flur / Eingangsbereich",
  "Sonstiges",
];

const INVENTORY_CONDITION_OPTIONS = [
  "Neu",
  "Neuwertig",
  "Gebraucht",
  "Beschädigt",
  "Defekt",
];

function buildConditionSelectOptions(currentValue) {
  const v = String(currentValue || "").trim();
  if (v && !INVENTORY_CONDITION_OPTIONS.includes(v)) {
    return [v, ...INVENTORY_CONDITION_OPTIONS];
  }
  return [...INVENTORY_CONDITION_OPTIONS];
}

function buildCategorySelectOptions(currentValue) {
  const v = String(currentValue || "").trim();
  if (v && !INVENTORY_CATEGORIES.includes(v)) {
    return [v, ...INVENTORY_CATEGORIES];
  }
  return [...INVENTORY_CATEGORIES];
}

function normalizeInventoryCategory(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (INVENTORY_CATEGORIES.includes(s)) return s;
  const lower = s.toLowerCase();
  const hit = INVENTORY_CATEGORIES.find((c) => c.toLowerCase() === lower);
  return hit || s;
}

const emptyItemForm = () => ({
  name: "",
  category: "",
  brand: "",
  total_quantity: "1",
  condition: "",
  purchase_price_chf: "",
  purchase_date: "",
  supplier_article_number: "",
  purchased_from: "",
  product_url: "",
  notes: "",
});

function draftToForm(draft) {
  if (!draft || typeof draft !== "object") return emptyItemForm();
  const pq = draft.total_quantity != null ? String(draft.total_quantity) : "1";
  const pp =
    draft.purchase_price_chf != null && draft.purchase_price_chf !== ""
      ? String(draft.purchase_price_chf)
      : "";
  const pd =
    draft.purchase_date != null && draft.purchase_date !== ""
      ? String(draft.purchase_date).slice(0, 10)
      : "";
  return {
    name: draft.name != null ? String(draft.name) : "",
    category: normalizeInventoryCategory(draft.category != null ? String(draft.category) : ""),
    brand: draft.brand != null ? String(draft.brand) : "",
    total_quantity: pq,
    condition: draft.condition != null ? String(draft.condition) : "",
    purchase_price_chf: pp,
    purchase_date: pd,
    supplier_article_number:
      draft.supplier_article_number != null ? String(draft.supplier_article_number) : "",
    purchased_from: draft.purchased_from != null ? String(draft.purchased_from) : "",
    product_url: draft.product_url != null ? String(draft.product_url) : "",
    notes: draft.notes != null ? String(draft.notes) : "",
  };
}

function inventoryBodyFromForm(f) {
  const tq = Math.max(1, parseInt(f.total_quantity, 10) || 1);
  const pu = parseOptionalProductUrl(f.product_url);
  if (pu.error) {
    return { _error: pu.error };
  }
  return {
    name: String(f.name || "").trim(),
    category: String(f.category || "").trim(),
    brand: String(f.brand || "").trim() || null,
    total_quantity: tq,
    condition: String(f.condition || "").trim(),
    purchase_price_chf:
      f.purchase_price_chf === "" || f.purchase_price_chf == null
        ? null
        : Number(f.purchase_price_chf),
    purchase_date: f.purchase_date ? f.purchase_date : null,
    supplier_article_number: String(f.supplier_article_number || "").trim() || null,
    purchased_from: String(f.purchased_from || "").trim() || null,
    product_url: pu.value,
    notes: String(f.notes || "").trim() || null,
  };
}

function FieldHint({ code }) {
  if (!code) return null;
  if (code === "review") {
    return (
      <p className="mt-0.5 text-[10px] text-amber-800 dark:text-amber-200/90">Bitte prüfen.</p>
    );
  }
  if (code === "missing") {
    return (
      <p className="mt-0.5 text-[10px] text-amber-800 dark:text-amber-200/90">
        Fehlt – bitte ergänzen.
      </p>
    );
  }
  return null;
}

/** Smart Import review only: derive Erkannt / Prüfen / Fehlt from hints + values. */
function importFieldReviewStatus(fieldKey, form, fieldHints) {
  const hint = fieldHints[fieldKey];
  const empty = (v) => v == null || String(v).trim() === "";

  if (fieldKey === "name") {
    if (empty(form.name)) return "fehlt";
    if (hint === "review") return "pruefen";
    return "erkannt";
  }
  if (fieldKey === "total_quantity") {
    const n = parseInt(form.total_quantity, 10);
    if (empty(form.total_quantity) || !Number.isFinite(n) || n < 1) return "fehlt";
    return "erkannt";
  }
  if (hint === "missing") return "fehlt";
  if (hint === "review") return "pruefen";
  if (!empty(form[fieldKey])) return "erkannt";
  return null;
}

function FieldReviewBadge({ status }) {
  if (!status) return null;
  const base =
    "mt-1 inline-flex rounded-md border px-1.5 py-0.5 text-[10px] font-semibold tracking-wide";
  if (status === "erkannt") {
    return (
      <span
        className={`${base} border-emerald-400/45 bg-emerald-50/90 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-950/45 dark:text-emerald-200/95`}
      >
        Erkannt
      </span>
    );
  }
  if (status === "pruefen") {
    return (
      <span
        className={`${base} border-amber-400/50 bg-amber-50/90 text-amber-950 dark:border-amber-500/35 dark:bg-amber-950/40 dark:text-amber-100`}
      >
        Prüfen
      </span>
    );
  }
  if (status === "fehlt") {
    return (
      <span
        className={`${base} border-rose-400/45 bg-rose-50/90 text-rose-950 dark:border-rose-500/35 dark:bg-rose-950/40 dark:text-rose-100`}
      >
        Fehlt
      </span>
    );
  }
  return null;
}

function InventoryItemFormFields({
  form,
  setForm,
  fieldHints = {},
  showReviewStatusBadges,
  statusReadOnlyLabel,
}) {
  const hintRow = (key) =>
    !showReviewStatusBadges && fieldHints[key] ? <FieldHint code={fieldHints[key]} /> : null;
  const badgeRow = (key) =>
    showReviewStatusBadges ? (
      <FieldReviewBadge status={importFieldReviewStatus(key, form, fieldHints)} />
    ) : null;

  return (
    <>
      <label className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
        Name *
        <input
          required
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-black/10 bg-slate-100 px-3 py-2 text-sm text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
        />
        {badgeRow("name")}
        {hintRow("name")}
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
          Gesamtmenge *
          <input
            type="number"
            min={1}
            value={form.total_quantity}
            onChange={(e) => setForm((f) => ({ ...f, total_quantity: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-black/10 bg-slate-100 px-3 py-2 text-sm text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
          />
          {badgeRow("total_quantity")}
        </label>
        <div className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
          Status
          <p className="mt-1 rounded-lg border border-black/10 border-dashed bg-slate-50 px-3 py-2 text-sm text-[#0f172a] dark:border-white/[0.1] dark:bg-[#111520]/60 dark:text-[#eef2ff]">
            {statusReadOnlyLabel || "—"}
          </p>
        </div>
      </div>
      <label className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
        Kategorie
        <select
          value={form.category}
          onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-black/10 bg-slate-100 px-3 py-2 text-sm text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
        >
          <option value="">—</option>
          {buildCategorySelectOptions(form.category).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {badgeRow("category")}
        {hintRow("category")}
      </label>
      <label className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
        Marke
        <input
          value={form.brand}
          onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-black/10 bg-slate-100 px-3 py-2 text-sm text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
        />
        {badgeRow("brand")}
        {hintRow("brand")}
      </label>
      <label className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
        Zustand
        <select
          value={form.condition}
          onChange={(e) => setForm((f) => ({ ...f, condition: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-black/10 bg-slate-100 px-3 py-2 text-sm text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
        >
          <option value="">—</option>
          {buildConditionSelectOptions(form.condition).map((c) => (
            <option key={c} value={c}>
              {INVENTORY_CONDITION_OPTIONS.includes(c) ? c : `${c} (bestehend)`}
            </option>
          ))}
        </select>
        {badgeRow("condition")}
        {hintRow("condition")}
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
          Anschaffung CHF
          <input
            type="number"
            step="0.01"
            value={form.purchase_price_chf}
            onChange={(e) =>
              setForm((f) => ({ ...f, purchase_price_chf: e.target.value }))
            }
            className="mt-1 w-full rounded-lg border border-black/10 bg-slate-100 px-3 py-2 text-sm text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
          />
          {badgeRow("purchase_price_chf")}
          {hintRow("purchase_price_chf")}
        </label>
        <label className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
          Kaufdatum
          <input
            type="date"
            value={form.purchase_date}
            onChange={(e) => setForm((f) => ({ ...f, purchase_date: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-black/10 bg-slate-100 px-3 py-2 text-sm text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
          />
          {badgeRow("purchase_date")}
          {hintRow("purchase_date")}
        </label>
      </div>
      <div className="rounded-lg border border-black/10 bg-slate-50 p-3 dark:border-white/[0.06] dark:bg-[#111520]/80">
        <p className="m-0 text-[10px] font-bold uppercase tracking-[0.9px] text-[#64748b] dark:text-[#93a4bf]">
          Purchase / Supplier Info
        </p>
        <div className="mt-3 grid grid-cols-1 gap-3">
          <label className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
            Lieferanten-Artikelnr.
            <input
              value={form.supplier_article_number}
              onChange={(e) =>
                setForm((f) => ({ ...f, supplier_article_number: e.target.value }))
              }
              placeholder="z. B. Hersteller-SKU"
              className="mt-1 w-full rounded-lg border border-black/10 bg-slate-100 px-3 py-2 text-sm text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
            />
            {badgeRow("supplier_article_number")}
            {hintRow("supplier_article_number")}
          </label>
          <label className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
            Gekauft bei
            <input
              value={form.purchased_from}
              onChange={(e) => setForm((f) => ({ ...f, purchased_from: e.target.value }))}
              placeholder="Händler / Lieferant"
              className="mt-1 w-full rounded-lg border border-black/10 bg-slate-100 px-3 py-2 text-sm text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
            />
            {badgeRow("purchased_from")}
            {hintRow("purchased_from")}
          </label>
          <label className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
            Produkt-URL
            <input
              type="url"
              inputMode="url"
              value={form.product_url}
              onChange={(e) => setForm((f) => ({ ...f, product_url: e.target.value }))}
              placeholder="https://…"
              className="mt-1 w-full rounded-lg border border-black/10 bg-slate-100 px-3 py-2 text-sm text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
            />
            {badgeRow("product_url")}
            {hintRow("product_url")}
          </label>
        </div>
      </div>
      <label className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
        Notizen
        <textarea
          rows={2}
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-black/10 bg-slate-100 px-3 py-2 text-sm text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
        />
        {badgeRow("notes")}
        {hintRow("notes")}
      </label>
    </>
  );
}

export default function AdminInventoryPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, skip: 0, limit: 50 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 50;
  const [units, setUnits] = useState([]);
  const [filterUnitId, setFilterUnitId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [roomFilterId, setRoomFilterId] = useState(ROOM_FILTER_ALL);
  const [unitAssignments, setUnitAssignments] = useState([]);
  const [unitRooms, setUnitRooms] = useState([]);
  const [loadingUnitAssignments, setLoadingUnitAssignments] = useState(false);
  /** Client-side filters (Zugeordnet/Fre): cached full list for pagination without re-fetching every page. */
  const assignmentListCacheRef = useRef(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyItemForm());
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [formErr, setFormErr] = useState("");

  const [smartImportOpen, setSmartImportOpen] = useState(false);
  const [importStep, setImportStep] = useState(1);
  const [importMode, setImportMode] = useState("url");
  const [importUrl, setImportUrl] = useState("");
  const [importText, setImportText] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importForm, setImportForm] = useState(emptyItemForm());
  const [importFieldHints, setImportFieldHints] = useState({});
  const [importMeta, setImportMeta] = useState(null);
  const [importWarnings, setImportWarnings] = useState([]);
  const [importErr, setImportErr] = useState("");
  const [importSaving, setImportSaving] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedQ(searchInput.trim());
      setSkip(0);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const collectAllFilteredInventory = useCallback(async () => {
    const base = {
      q: debouncedQ || undefined,
      category: categoryFilter || undefined,
      unit_id: filterUnitId || undefined,
    };
    const fetchAllPages = async (params) => {
      let all = [];
      let off = 0;
      const MAX_OFF = 200 * 100;
      while (off < MAX_OFF) {
        const batch = await fetchAdminInventory({ ...params, skip: off, limit: 200 });
        const items = batch && Array.isArray(batch.items) ? batch.items : [];
        all = all.concat(items);
        if (items.length < 200) break;
        off += 200;
      }
      return all;
    };

    if (statusFilter === "assigned" || statusFilter === "free") {
      const cacheKey = `${debouncedQ}|${categoryFilter}|${filterUnitId}|${statusFilter}`;
      if (assignmentListCacheRef.current?.key === cacheKey) {
        return assignmentListCacheRef.current.filtered;
      }
      const allItems = await fetchAllPages(base);
      const filtered =
        statusFilter === "assigned"
          ? allItems.filter((row) => Number(row.assigned_total || 0) > 0)
          : allItems.filter((row) => Number(row.available || 0) > 0);
      assignmentListCacheRef.current = { key: cacheKey, filtered };
      return filtered;
    }

    const statusParam =
      statusFilter !== "all" &&
      ["stored", "damaged", "repair", "retired"].includes(statusFilter)
        ? statusFilter
        : undefined;
    return fetchAllPages({
      ...base,
      status: statusParam,
      has_open_damage: statusFilter === "open_damage" ? true : undefined,
    });
  }, [debouncedQ, categoryFilter, filterUnitId, statusFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const sum = await fetchAdminInventorySummary().catch(() => null);
      setSummary(sum && typeof sum === "object" ? sum : null);

      if (filterUnitId && roomFilterId !== ROOM_FILTER_ALL) {
        const full = await collectAllFilteredInventory();
        const filtered = applyInventoryRoomFilter(full, unitAssignments, roomFilterId);
        setData({
          items: filtered,
          total: filtered.length,
          skip: 0,
          limit: filtered.length || limit,
        });
        return;
      }

      const qParam = debouncedQ || undefined;
      const catParam = categoryFilter || undefined;
      const uid = filterUnitId || undefined;
      const base = {
        q: qParam,
        category: catParam,
        unit_id: uid,
      };

      if (statusFilter === "assigned" || statusFilter === "free") {
        const cacheKey = `${debouncedQ}|${categoryFilter}|${filterUnitId}|${statusFilter}`;
        let filtered;
        if (assignmentListCacheRef.current?.key === cacheKey) {
          filtered = assignmentListCacheRef.current.filtered;
        } else {
          let allItems = [];
          let off = 0;
          const MAX_OFF = 200 * 100;
          while (off < MAX_OFF) {
            const batch = await fetchAdminInventory({
              ...base,
              skip: off,
              limit: 200,
            });
            const items = batch && Array.isArray(batch.items) ? batch.items : [];
            allItems = allItems.concat(items);
            if (items.length < 200) break;
            off += 200;
          }
          filtered =
            statusFilter === "assigned"
              ? allItems.filter((row) => Number(row.assigned_total || 0) > 0)
              : allItems.filter((row) => Number(row.available || 0) > 0);
          assignmentListCacheRef.current = { key: cacheKey, filtered };
        }
        const total = filtered.length;
        const pageItems = filtered.slice(skip, skip + limit);
        setData({ items: pageItems, total, skip, limit });
      } else {
        assignmentListCacheRef.current = null;
        // Lager + lifecycle: same field as KPI cards — InventoryItem.status via GET /inventory?status=
        // (get_inventory_summary: damaged/repair/retired counts use status, not condition)
        const statusParam =
          statusFilter !== "all" &&
          ["stored", "damaged", "repair", "retired"].includes(statusFilter)
            ? statusFilter
            : undefined;
        const inv = await fetchAdminInventory({
          ...base,
          status: statusParam,
          has_open_damage: statusFilter === "open_damage" ? true : undefined,
          skip,
          limit,
        });
        setData(
          inv && typeof inv === "object" && Array.isArray(inv.items)
            ? inv
            : { items: [], total: 0, skip: 0, limit }
        );
      }
    } catch (e) {
      setError(e?.message || "Laden fehlgeschlagen.");
      setData({ items: [], total: 0, skip: 0, limit });
    } finally {
      setLoading(false);
    }
  }, [
    skip,
    debouncedQ,
    statusFilter,
    categoryFilter,
    filterUnitId,
    roomFilterId,
    unitAssignments,
    collectAllFilteredInventory,
  ]);

  const handleExportCsv = useCallback(async () => {
    setExporting(true);
    setError("");
    try {
      const full = await collectAllFilteredInventory();
      const rows =
        filterUnitId && roomFilterId !== ROOM_FILTER_ALL
          ? applyInventoryRoomFilter(full, unitAssignments, roomFilterId)
          : full;
      if (!rows || rows.length === 0) return;
      downloadInventoryCsv(rows);
    } catch (e) {
      setError(e?.message || "Export fehlgeschlagen.");
    } finally {
      setExporting(false);
    }
  }, [collectAllFilteredInventory, filterUnitId, roomFilterId, unitAssignments]);

  useEffect(() => {
    fetchAdminUnits()
      .then((data) =>
        setUnits(Array.isArray(data) ? data.map((u) => normalizeUnit(u)) : [])
      )
      .catch(() => setUnits([]));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!filterUnitId) {
      setUnitAssignments([]);
      setUnitRooms([]);
      setRoomFilterId(ROOM_FILTER_ALL);
      return;
    }
    let cancelled = false;
    setLoadingUnitAssignments(true);
    Promise.all([
      fetchUnitInventoryAssignments(filterUnitId).catch(() => []),
      fetchAdminRooms(filterUnitId).catch(() => []),
    ])
      .then(([asg, rms]) => {
        if (cancelled) return;
        setUnitAssignments(Array.isArray(asg) ? asg : []);
        setUnitRooms(Array.isArray(rms) ? rms : []);
      })
      .finally(() => {
        if (!cancelled) setLoadingUnitAssignments(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filterUnitId]);

  function openCreate() {
    setForm(emptyItemForm());
    setFormErr("");
    setCreateOpen(true);
  }

  function openSmartImport() {
    setImportStep(1);
    setImportMode("url");
    setImportUrl("");
    setImportText("");
    setImportForm(emptyItemForm());
    setImportFieldHints({});
    setImportMeta(null);
    setImportWarnings([]);
    setImportErr("");
    setSmartImportOpen(true);
  }

  function closeSmartImport() {
    setSmartImportOpen(false);
    setImportErr("");
    setPreviewLoading(false);
  }

  async function runImportPreview() {
    setImportErr("");
    if (importMode === "url") {
      const u = String(importUrl || "").trim();
      if (!u) {
        setImportErr("Bitte eine URL eingeben.");
        return;
      }
      setPreviewLoading(true);
      try {
        const data = await postInventoryImportPreview({ source_type: "url", url: u });
        setImportForm(draftToForm(data.draft));
        setImportFieldHints(data.field_hints && typeof data.field_hints === "object" ? data.field_hints : {});
        setImportMeta(data.meta || null);
        setImportWarnings(Array.isArray(data.warnings) ? data.warnings : []);
        setImportStep(2);
      } catch (err) {
        setImportErr(err?.message || "Vorschau fehlgeschlagen.");
      } finally {
        setPreviewLoading(false);
      }
    } else {
      const t = String(importText || "").trim();
      if (!t) {
        setImportErr("Bitte Text eingeben.");
        return;
      }
      setPreviewLoading(true);
      try {
        const data = await postInventoryImportPreview({ source_type: "text", text: t });
        setImportForm(draftToForm(data.draft));
        setImportFieldHints(data.field_hints && typeof data.field_hints === "object" ? data.field_hints : {});
        setImportMeta(data.meta || null);
        setImportWarnings(Array.isArray(data.warnings) ? data.warnings : []);
        setImportStep(2);
      } catch (err) {
        setImportErr(err?.message || "Vorschau fehlgeschlagen.");
      } finally {
        setPreviewLoading(false);
      }
    }
  }

  async function submitImportSave(e) {
    e.preventDefault();
    const b = inventoryBodyFromForm(importForm);
    if (b._error) {
      setImportErr(b._error);
      return;
    }
    if (!b.name) {
      setImportErr("Name erforderlich.");
      return;
    }
    setImportSaving(true);
    setImportErr("");
    try {
      await createInventoryItem(b);
      closeSmartImport();
      assignmentListCacheRef.current = null;
      load();
    } catch (err) {
      setImportErr(err?.message || "Fehler.");
    } finally {
      setImportSaving(false);
    }
  }

  function continueImportAsManualForm() {
    setForm({ ...importForm });
    setFormErr("");
    closeSmartImport();
    setCreateOpen(true);
  }

  async function submitCreate(e) {
    e.preventDefault();
    const b = inventoryBodyFromForm(form);
    if (b._error) {
      setFormErr(b._error);
      return;
    }
    if (!b.name) {
      setFormErr("Name erforderlich.");
      return;
    }
    setSaving(true);
    setFormErr("");
    try {
      await createInventoryItem(b);
      setCreateOpen(false);
      assignmentListCacheRef.current = null;
      load();
    } catch (err) {
      setFormErr(err?.message || "Fehler.");
    } finally {
      setSaving(false);
    }
  }

  const canNext = skip + limit < data.total;
  const canPrev = skip > 0;
  const showRoomQtyCol = Boolean(filterUnitId) && roomFilterId !== ROOM_FILTER_ALL;
  const roomOptions = useMemo(
    () => buildRoomOptions(unitAssignments, unitRooms),
    [unitAssignments, unitRooms]
  );

  const pagePurchaseTotalChf = useMemo(() => {
    return (data.items || []).reduce((sum, row) => {
      const price = Number(row.purchase_price_chf || 0);
      const qty =
        row.filtered_assigned_quantity != null
          ? Number(row.filtered_assigned_quantity)
          : Number(row.total_quantity || 1);
      return sum + price * qty;
    }, 0);
  }, [data.items]);

  const hasActiveFilters =
    Boolean(searchInput) ||
    statusFilter !== "all" ||
    Boolean(filterUnitId) ||
    Boolean(categoryFilter) ||
    roomFilterId !== ROOM_FILTER_ALL;

  const resetFilters = useCallback(() => {
    setSearchInput("");
    setStatusFilter("all");
    setFilterUnitId("");
    setRoomFilterId(ROOM_FILTER_ALL);
    setCategoryFilter("");
    setSkip(0);
  }, []);

  const kpiSku = loading ? "—" : summary != null ? summary.total_skus : "—";
  const kpiPieces = loading ? "—" : summary != null ? summary.total_pieces : "—";
  const kpiAssigned = loading ? "—" : summary != null ? summary.assigned_total : "—";
  const kpiFree = loading ? "—" : summary != null ? summary.available_total : "—";
  const kpiTotalValue =
    loading || !summary
      ? "—"
      : summary.total_purchase_value_chf != null
        ? formatChf(summary.total_purchase_value_chf)
        : summary.total_anschaffung_chf != null
          ? formatChf(summary.total_anschaffung_chf)
          : "—";

  const kpiDamaged =
    loading || summary == null ? "—" : String(summary.damaged_count ?? "—");
  const kpiRetired =
    loading || summary == null ? "—" : String(summary.retired_count ?? "—");
  const kpiOpenDamage =
    loading || summary == null ? "—" : String(summary.open_damage_reports ?? "—");
  const kpiAvgLifespan =
    loading || summary == null ? "—" : formatDaysToLabel(summary.avg_lifespan_days);
  const kpiAvgAge = loading || summary == null ? "—" : formatDaysToLabel(summary.avg_age_days);

  return (
    <div
      className="flex flex-col gap-5"
      data-testid="admin-inventory-page"
      data-admin-page="inventar"
    >
      {/* No -m-6 escape any more. AdminLayout wraps the outlet in p-4 md:p-10,
          so pulling back 24px left the page bleeding 8px OUTSIDE its container
          on mobile and fighting the shell on desktop. Sitting inside the shell
          also means the page finally inherits the theme instead of painting
          itself dark: light mode is reachable from the sidebar toggle, and the
          overview used to ignore it. */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        {/* S7c: an icon tile leads the title, the way the reference opens the
            page. Decorative only — the <h1> remains the accessible heading. */}
        <div className="flex min-w-0 items-center gap-3">
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-primary/10 text-primary"
            aria-hidden
          >
            <Package className="h-[22px] w-[22px]" />
          </span>
          <div className="flex min-w-0 flex-col gap-0.5">
            <h1 className="m-0 text-[20px] font-semibold leading-tight text-foreground">Inventar</h1>
            <p className="m-0 max-w-2xl text-[12.5px] text-muted-foreground">
              Artikel, Bestand, Zuordnungen und Schäden verwalten.
            </p>
          </div>
        </div>
        {/* Wraps rather than collapses: every action stays reachable on a phone. */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={openSmartImport}
            className="rounded-[8px] border border-border bg-card px-3 py-2 text-[12px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Smart Import
          </button>
          <button
            type="button"
            onClick={openCreate}
            className="rounded-[8px] bg-primary px-4 py-2 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            + Neuer Artikel
          </button>
        </div>
      </header>

      {error ? (
        <p role="alert" className="m-0 rounded-[12px] border border-border bg-card px-4 py-3 text-[12.5px] text-destructive">
          {error}
        </p>
      ) : null}

      <section aria-label="Bestandskennzahlen" className="flex flex-col gap-2">
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-5">
          <KpiCard label="Artikel" value={kpiSku} hint="Erfasste Artikel (SKUs)" icon={Package} tone="primary" />
          <KpiCard label="Stück gesamt" value={kpiPieces} hint="Summe aller Stückzahlen" icon={Layers} />
          <KpiCard label="Zugeordnet" value={kpiAssigned} hint="Auf Units verteilt" icon={MapPin} tone="success" />
          <KpiCard label="Frei" value={kpiFree} hint="Noch nicht zugeordnet" icon={Boxes} tone="warning" />
          <KpiCard label="Gesamtwert" value={kpiTotalValue} hint="Summe aller Anschaffungspreise" icon={Banknote} tone="primary" />
        </div>
        {/* Status indicators, deliberately a tier below the five above. Both
            labels name their UNIT, because the two numbers count different
            things and the old row implied they were the same kind: damaged_count
            counts ARTICLES (a whole SKU flips to "damaged" however many pieces
            broke), open_damage_reports counts REPORTS. */}
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
          <KpiCard
            label="Beschädigte Artikel"
            value={kpiDamaged}
            hint="Artikel mit Schadensstatus"
            icon={AlertTriangle}
            tone="warning"
          />
          <KpiCard
            label="Offene Schadensfälle"
            value={kpiOpenDamage}
            hint="Schadensmeldungen"
            icon={Wrench}
            tone="warning"
          />
          {/* The reference puts Ø Alter in this band. It is a real figure, so it
              earns the tile — unlike "In Reparatur", which the reference also
              shows and which we still refuse (S4): that status has no writer in
              the backend, so the card could only ever read 0. */}
          <KpiCard label="Ø Alter" value={kpiAvgAge} hint="Aktive Artikel mit Kaufdatum" icon={Clock} />
        </div>
        {/* Quiet meta line rather than cards. These three are real numbers and
            stay visible, but "Ausgemustert" rests on write-off semantics that
            are knowingly incomplete (a write-off retires the whole SKU without
            reducing any quantity), so promoting it would advertise a figure the
            data model cannot yet stand behind. No "In Reparatur" tile at all:
            InventoryItem.status == "repair" has no writer anywhere in the
            backend, so that card was structurally always 0. */}
        <dl className="m-0 flex flex-wrap items-center gap-x-6 gap-y-1 px-1 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <dt className="m-0">Ausgemustert</dt>
            <dd className="m-0 font-medium text-foreground">{kpiRetired}</dd>
          </div>
          <div className="flex items-center gap-1.5">
            <dt className="m-0">Ø Lebensdauer</dt>
            <dd className="m-0 font-medium text-foreground">{kpiAvgLifespan}</dd>
          </div>
        </dl>
      </section>

      {/* S7c: the reference's filter surface is ONE compact line — search and
          the four selects on the left, the result count and the CSV export on
          the right. The per-control <label> elements are gone; every control
          carries the SAME accessible name as an aria-label, because the
          characterization suite resolves them by that name (C1: "Suche",
          "Status-Filter", "Unit / Apartment", "Raum-Filter", "Kategorie") and
          those names are the contract, not the visible text.

          The filter behaviour itself is untouched: same state, same setSkip(0)
          resets, same disabled rule on the room select, same reset button. */}
      <section aria-label="Filter" className="rounded-[12px] border border-border bg-card p-3">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
          <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-[minmax(180px,1.5fr)_repeat(4,minmax(128px,1fr))]">
            <input
              id="inv-search"
              type="search"
              aria-label="Suche"
              value={searchInput}
              placeholder="Artikelname, Nr., Lieferant…"
              className={FILTER_CONTROL_CLASS}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <select
              id="inv-status"
              value={statusFilter}
              className={FILTER_CONTROL_CLASS}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setSkip(0);
              }}
              aria-label="Status-Filter"
            >
              <option value="all">Alle Status</option>
              <option value="assigned">Zugeordnet</option>
              <option value="free">Frei</option>
              <option value="stored">Lager</option>
              <option value="damaged">Beschädigt</option>
              {/* "In Reparatur" stays selectable and stays unpromoted. The item
                  status it filters on has no writer in the backend, so it
                  matches nothing today -- but the value is still part of the
                  CSV label contract (inventoryExport.test.js:75) and of the
                  audit vocabulary, so quietly dropping the option here would be
                  a product decision this slice is not allowed to take. */}
              <option value="repair">In Reparatur</option>
              <option value="retired">Ausgemustert</option>
              <option value="open_damage">Offene Schäden</option>
            </select>
            <select
              id="inv-unit"
              value={filterUnitId}
              className={FILTER_CONTROL_CLASS}
              aria-label="Unit / Apartment"
              onChange={(e) => {
                setFilterUnitId(e.target.value);
                setRoomFilterId(ROOM_FILTER_ALL);
                setSkip(0);
              }}
            >
              <option value="">Alle Units</option>
              {units.map((u) => {
                const id = String(u.id ?? u.unitId ?? "");
                const label = `${getUnitHeadingLabel(u)} — ${getUnitAddressSubtitle(u)}`;
                return (
                  <option key={id || label} value={id}>
                    {label}
                  </option>
                );
              })}
            </select>
            <select
              id="inv-room"
              value={roomFilterId}
              disabled={!filterUnitId || loadingUnitAssignments}
              className={`${FILTER_CONTROL_CLASS} disabled:cursor-not-allowed disabled:opacity-50`}
              onChange={(e) => {
                setRoomFilterId(e.target.value);
                setSkip(0);
              }}
              aria-label="Raum-Filter"
              title={!filterUnitId ? "Zuerst Unit wählen" : undefined}
            >
              {!filterUnitId ? (
                <option value="all">Zuerst Unit wählen</option>
              ) : (
                roomOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))
              )}
            </select>
            <select
              id="inv-category"
              value={categoryFilter}
              className={FILTER_CONTROL_CLASS}
              aria-label="Kategorie"
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setSkip(0);
              }}
            >
              <option value="">Alle Kategorien</option>
              {INVENTORY_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-border pt-3 xl:border-0 xl:border-l xl:pl-4 xl:pt-0">
            <span className="whitespace-nowrap text-[11.5px] text-muted-foreground">
              {loading ? "Lade…" : `${data.total} Artikel`}
            </span>
            {/* Only offered once something is actually filtered: a permanently
                visible "reset" on an unfiltered list is a button that does
                nothing. It sets the same state the controls above set -- no
                filter logic of its own. */}
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={resetFilters}
                className="rounded-[8px] border border-border bg-transparent px-3 py-1.5 text-[11.5px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Filter zurücksetzen
              </button>
            ) : null}
            {/* Moved out of the page header: the reference puts the export next
                to the result count, where it reads as "export THIS result". */}
            <button
              type="button"
              onClick={handleExportCsv}
              disabled={loading || exporting || data.total === 0}
              className="whitespace-nowrap rounded-[8px] border border-border bg-card px-3 py-1.5 text-[11.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? "Export läuft…" : "CSV exportieren"}
            </button>
          </div>
        </div>
      </section>

        <section aria-label="Artikelliste" className="overflow-hidden rounded-[12px] border border-border bg-card">
          <div className="flex flex-col gap-2 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="m-0 text-[13px] font-semibold text-foreground">Alle Artikel</h2>
              <p className="m-0 mt-0.5 text-[11px] text-muted-foreground">
                Artikel mit Gesamtmenge und Zuordnung auf Units
              </p>
            </div>
            <span className="w-fit rounded-[6px] border border-border bg-muted px-2.5 py-0.5 text-[10.5px] text-muted-foreground">
              {data.total} Einträge
            </span>
          </div>
          {loading ? (
            /* Skeleton rows rather than a "Lade …" line: the line collapsed the
               surface to one row's height and the table snapped back when the
               response landed. */
            <div className="flex flex-col gap-2 p-4" aria-busy="true" aria-live="polite">
              <span className="sr-only">Artikel werden geladen…</span>
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="h-9 animate-pulse rounded-[8px] bg-muted" />
              ))}
            </div>
          ) : data.items.length === 0 ? (
            /* Two different situations, two different answers. Offering "reset
               filters" to somebody who simply has no inventory yet would be
               advice that cannot help them. */
            <div className="px-4 py-10 text-center">
              <p className="m-0 text-[13px] font-medium text-foreground">
                {hasActiveFilters ? "Keine Treffer" : "Noch keine Artikel erfasst"}
              </p>
              <p className="m-0 mt-1 text-[12px] text-muted-foreground">
                {hasActiveFilters
                  ? "Für diese Filterkombination gibt es keine Artikel."
                  : "Erfasse den ersten Artikel oder importiere eine bestehende Liste."}
              </p>
              {hasActiveFilters ? (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-4 rounded-[8px] border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Filter zurücksetzen
                </button>
              ) : null}
            </div>
          ) : (
              <>
                <InventoryList
                  items={data.items}
                  showRoomQtyCol={showRoomQtyCol}
                  roomQtyLabel={
                    roomFilterId === ROOM_FILTER_SHARED ? "Gemeinschaftlich" : "Menge im Raum"
                  }
                  pageTotalChf={pagePurchaseTotalChf}
                  onOpenItem={(id) => navigate(`/admin/inventory/${id}`)}
                />
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-[14px] py-[10px]">
                  <span className="text-[11px] text-muted-foreground">
                    {data.total} Einträge · {skip + 1}–{Math.min(skip + data.items.length, data.total)}
                  </span>
                  {!showRoomQtyCol && (
                  <div className="flex gap-[6px]">
                    <button
                      type="button"
                      disabled={!canPrev}
                      onClick={() => setSkip((s) => Math.max(0, s - limit))}
                      className="rounded-[6px] border border-border bg-muted px-[12px] py-[3px] text-[10px] text-muted-foreground disabled:opacity-40"
                    >
                      Zurück
                    </button>
                    <button
                      type="button"
                      disabled={!canNext}
                      onClick={() => setSkip((s) => s + limit)}
                      className="rounded-[6px] border border-border bg-muted px-[12px] py-[3px] text-[10px] text-muted-foreground disabled:opacity-40"
                    >
                      Weiter
                    </button>
                  </div>
                  )}
                </div>
              </>
            )}
        </section>

      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/60">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[14px] border border-black/10 bg-white p-6 dark:border-white/[0.07] dark:bg-[#141824]">
            <h4 className="m-0 text-lg font-semibold text-[#0f172a] dark:text-[#eef2ff]">Neuer Artikel</h4>
            {formErr ? <p className="mt-2 text-sm text-[#f87171]">{formErr}</p> : null}
            <form
              onSubmit={submitCreate}
              className="mt-4 grid grid-cols-1 gap-3 text-[#0f172a] dark:text-[#eef2ff]"
            >
              <InventoryItemFormFields
                form={form}
                setForm={setForm}
                statusReadOnlyLabel={`Status: ${inventoryStatusLabelDe("stored")} (automatisch)`}
              />
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setCreateOpen(false);
                    setFormErr("");
                  }}
                  className="rounded-lg border border-black/10 px-4 py-2 text-sm text-[#0f172a] hover:bg-slate-100 dark:border-white/[0.12] dark:text-[#eef2ff] dark:hover:bg-white/[0.04]"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-[#5b8cff] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {saving ? "…" : "Speichern"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {smartImportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/60">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[14px] border border-black/10 bg-white p-6 dark:border-white/[0.07] dark:bg-[#141824]">
            <h4 className="m-0 text-lg font-semibold text-[#0f172a] dark:text-[#eef2ff]">Smart Import</h4>
            {importErr ? <p className="mt-2 text-sm text-[#f87171]">{importErr}</p> : null}

            {importStep === 1 ? (
              <div className="mt-4 space-y-4 text-[#0f172a] dark:text-[#eef2ff]">
                <p className="m-0 text-[13px] leading-relaxed text-[#64748b] dark:text-[#93a4bf]">
                  Fügen Sie eine Produkt-URL oder Freitext ein. Es wird eine deterministische Vorschau
                  erzeugt — ohne Live-Analyse.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setImportMode("url")}
                    className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
                      importMode === "url"
                        ? "bg-[#5b8cff] text-white"
                        : "border border-black/10 bg-slate-100 text-[#0f172a] dark:border-white/[0.12] dark:bg-[#111520] dark:text-[#eef2ff]"
                    }`}
                  >
                    URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setImportMode("text")}
                    className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
                      importMode === "text"
                        ? "bg-[#5b8cff] text-white"
                        : "border border-black/10 bg-slate-100 text-[#0f172a] dark:border-white/[0.12] dark:bg-[#111520] dark:text-[#eef2ff]"
                    }`}
                  >
                    Text
                  </button>
                </div>
                {importMode === "url" ? (
                  <label className="block text-[11px] text-[#64748b] dark:text-[#93a4bf]">
                    Produkt-URL
                    <input
                      type="url"
                      inputMode="url"
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      placeholder="https://…"
                      className="mt-1 w-full rounded-lg border border-black/10 bg-slate-100 px-3 py-2 text-sm text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
                    />
                  </label>
                ) : (
                  <label className="block text-[11px] text-[#64748b] dark:text-[#93a4bf]">
                    Text
                    <textarea
                      rows={6}
                      value={importText}
                      onChange={(e) => setImportText(e.target.value)}
                      placeholder="Produktbeschreibung, Notizen, Preis …"
                      className="mt-1 w-full rounded-lg border border-black/10 bg-slate-100 px-3 py-2 text-sm text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
                    />
                  </label>
                )}
                <div className="flex flex-wrap justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={closeSmartImport}
                    className="rounded-lg border border-black/10 px-4 py-2 text-sm text-[#0f172a] hover:bg-slate-100 dark:border-white/[0.12] dark:text-[#eef2ff] dark:hover:bg-white/[0.04]"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    disabled={previewLoading}
                    onClick={runImportPreview}
                    className="rounded-lg bg-[#5b8cff] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {previewLoading ? "…" : "Vorschläge anzeigen"}
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={submitImportSave}
                className="mt-4 grid grid-cols-1 gap-3 text-[#0f172a] dark:text-[#eef2ff]"
              >
                <div
                  role="status"
                  className="space-y-2 rounded-lg border border-amber-400/45 bg-amber-50/95 px-3 py-3 text-[12px] text-amber-950 dark:border-amber-500/35 dark:bg-amber-950/40 dark:text-amber-100"
                >
                  <h3 className="m-0 text-[13px] font-semibold leading-snug text-amber-950 dark:text-amber-50">
                    Automatisch erkannte Daten (bitte prüfen)
                  </h3>
                  <p className="m-0 leading-relaxed text-amber-900/95 dark:text-amber-100/95">
                    Bitte prüfen Sie alle importierten Felder vor dem Speichern.
                  </p>
                </div>
                {importWarnings.length > 0 ? (
                  <ul className="m-0 list-inside list-disc space-y-1 text-[12px] text-[#64748b] dark:text-[#93a4bf]">
                    {importWarnings.map((w, i) => (
                      <li key={i}>
                        {typeof w === "object" && w != null && w.message
                          ? String(w.message)
                          : String(w)}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {importMeta && typeof importMeta === "object" ? (
                  <p className="m-0 text-[11px] text-[#64748b] dark:text-[#93a4bf]">
                    Quelle: {importMeta.source_type === "text" ? "Text" : "URL"}
                    {importMeta.source_excerpt
                      ? ` · ${String(importMeta.source_excerpt).slice(0, 120)}${
                          String(importMeta.source_excerpt).length > 120 ? "…" : ""
                        }`
                      : ""}
                  </p>
                ) : null}
                <InventoryItemFormFields
                  form={importForm}
                  setForm={setImportForm}
                  fieldHints={importFieldHints}
                  showReviewStatusBadges
                  statusReadOnlyLabel={`Status: ${inventoryStatusLabelDe("stored")} (automatisch)`}
                />
                <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:flex-wrap sm:justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setImportStep(1);
                      setImportErr("");
                    }}
                    className="rounded-lg border border-black/10 px-4 py-2 text-sm text-[#0f172a] hover:bg-slate-100 dark:border-white/[0.12] dark:text-[#eef2ff] dark:hover:bg-white/[0.04]"
                  >
                    Zurück
                  </button>
                  <button
                    type="button"
                    onClick={continueImportAsManualForm}
                    className="rounded-lg border border-black/10 px-4 py-2 text-sm text-[#0f172a] hover:bg-slate-100 dark:border-white/[0.12] dark:text-[#eef2ff] dark:hover:bg-white/[0.04]"
                  >
                    Als normales Formular weiterbearbeiten
                  </button>
                  <button
                    type="submit"
                    disabled={importSaving}
                    className="rounded-lg bg-[#5b8cff] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {importSaving ? "…" : "Artikel speichern"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
